/**
 * Template Connection Resolver
 *
 * Resolves a template's declared connection requirements against the user's
 * existing connection references.  All functions are pure — no store access,
 * no side effects.
 */

import type { TemplateConnectionRequirement } from '@/types/template';
import type { ConnectionReferences } from '@/types/connectionReference';
import type { WorkflowDefinition, Action, AgentAction, AgentTool, IfAction, ForeachAction, SwitchAction, UntilAction } from '@/types/workflowDefinition';
import { isAgentAction, isIfAction, isForeachAction, isSwitchAction, isUntilAction } from '@/types/workflowDefinition';
import { extractConnectorName } from '@/utils/connections';

/**
 * Look up connections compatible with a given apiId in a references map.
 *
 * Injected by callers so this module stays free of service imports — keeps
 * `utils/templates/` strictly within the layering rule
 * (`utils/* → @/types/* | @/utils/*` only). Callers wire this to
 * `findCompatibleConnectionsByApiId` from `@/services/connectionMatchingService`.
 */
export type FindCompatibleConnections = (
  apiId: string,
  references: ConnectionReferences,
) => ReadonlyArray<{ referenceKey: string; displayName: string }>;

// =============================================================================
// PUBLIC TYPES
// =============================================================================

/** Result of matching a single connection requirement */
export interface ConnectionResolution {
  requirement: TemplateConnectionRequirement;
  /** Connector name extracted from apiId (e.g., 'sql', 'azureopenai') — used as the key for connection mapping */
  connectorName: string;
  status: 'auto-matched' | 'needs-selection' | 'unresolved';
  /** The single auto-matched connection reference key (when exactly 1 match) */
  matchedReferenceKey?: string;
  /** Display name of the matched connection */
  matchedDisplayName?: string;
  /** All compatible connections (when multiple matches exist) */
  compatibleConnections?: Array<{ referenceKey: string; displayName: string }>;
}

/** Full resolution result for all template requirements */
export interface TemplateConnectionResolutionResult {
  /** Requirements that were automatically satisfied (1:1 match) */
  autoMatched: ConnectionResolution[];
  /** Requirements where user must choose between multiple existing connections */
  needsSelection: ConnectionResolution[];
  /** Requirements where no compatible connection exists */
  unresolved: ConnectionResolution[];
  /** Whether ALL required connections have at least one compatible match (auto-matched or needs-selection) */
  allRequiredHaveMatches: boolean;
  /** Whether the template can be applied immediately (no selection needed for required connections) */
  canAutoApply: boolean;
}

// =============================================================================
// PUBLIC FUNCTIONS
// =============================================================================

/**
 * Resolve template connection requirements against existing connection references.
 *
 * For each requirement:
 * - 1 compatible match  → `auto-matched`
 * - 2+ compatible matches → `needs-selection` (all candidates included)
 * - 0 compatible matches  → `unresolved`
 *
 * @param requirements  - Connection requirements declared by the template
 * @param existingReferences - Current `ConnectionReferences` from the connection store
 * @param findCompatibleConnections - Injected lookup function (see `FindCompatibleConnections`)
 */
export function resolveTemplateConnections(
  requirements: TemplateConnectionRequirement[],
  existingReferences: ConnectionReferences,
  findCompatibleConnections: FindCompatibleConnections,
): TemplateConnectionResolutionResult {
  const autoMatched: ConnectionResolution[] = [];
  const needsSelection: ConnectionResolution[] = [];
  const unresolved: ConnectionResolution[] = [];

  for (const requirement of requirements) {
    const connectorName = extractConnectorName(requirement.apiId);
    // Pass the full apiId to preserve the distinction between
    // /managedApis/sql and /serviceProviders/sql
    const matches = findCompatibleConnections(requirement.apiId, existingReferences);

    if (matches.length === 0) {
      unresolved.push({ requirement, connectorName, status: 'unresolved' });
    } else if (matches.length === 1) {
      const match = matches[0]!;
      autoMatched.push({
        requirement,
        connectorName,
        status: 'auto-matched',
        matchedReferenceKey: match.referenceKey,
        matchedDisplayName: match.displayName,
      });
    } else {
      needsSelection.push({
        requirement,
        connectorName,
        status: 'needs-selection',
        compatibleConnections: matches.map((m) => ({
          referenceKey: m.referenceKey,
          displayName: m.displayName,
        })),
      });
    }
  }

  // All template connections are implicitly required
  const allRequiredHaveMatches = requirements.every(
    (req) => autoMatched.some((r) => r.requirement?.apiId?.toLowerCase() === req.apiId?.toLowerCase()) ||
     needsSelection.some((r) => r.requirement?.apiId?.toLowerCase() === req.apiId?.toLowerCase())
  );

  // Can auto-apply if all requirements are auto-matched (no user selection needed)
  const canAutoApply = allRequiredHaveMatches && needsSelection.length === 0;

  return { autoMatched, needsSelection, unresolved, allRequiredHaveMatches, canAutoApply };
}

/**
 * Build a partial `ConnectionReferences` map from resolved connections.
 *
 * Combines auto-matched resolutions with optional user selections (a map from
 * `connectorName` to the chosen `referenceKey`).  The result can be used to
 * patch a template workflow definition before applying it.
 *
 * @param result         - The resolution result produced by `resolveTemplateConnections`
 * @param existingReferences - The full connection references to look up reference data
 * @param userSelections - Optional map of connectorName → chosen referenceKey
 */
export function buildConnectionReferencesFromResolutions(
  result: TemplateConnectionResolutionResult,
  existingReferences: ConnectionReferences,
  userSelections?: Map<string, string>
): ConnectionReferences {
  const partial: ConnectionReferences = {};

  // Include all auto-matched references
  for (const resolution of result.autoMatched) {
    const refKey = resolution.matchedReferenceKey;
    if (refKey && existingReferences[refKey]) {
      partial[refKey] = existingReferences[refKey]!;
    }
  }

  // Include user selections for needs-selection resolutions
  if (userSelections) {
    for (const resolution of result.needsSelection) {
      const chosenKey = userSelections.get(resolution.connectorName);
      if (chosenKey && existingReferences[chosenKey]) {
        partial[chosenKey] = existingReferences[chosenKey]!;
      }
    }
  }

  return partial;
}

/**
 * Patch a workflow definition's actions with real connection reference keys.
 *
 * Walks all actions (including nested If/Foreach/Switch/Until scopes) and
 * replaces placeholder connection names with the resolved reference keys.
 *
 * The mapping key is typically the template's `referenceKey` (e.g.
 * `"sql_myWorkflow"`) or a `connectorName` fallback. Keys are matched
 * against the action's current connection reference name — first by exact
 * match, then by normalized (case-insensitive, alphanumeric-only) match.
 * If no mapping entry matches, the action is left unchanged.
 *
 * @param workflowDefinition - Source workflow definition (not mutated)
 * @param connectionMap      - Map of templateReferenceKey → resolvedReferenceKey to apply
 * @returns A deep-cloned workflow definition with patched connection references
 */
export function patchWorkflowConnections(
  workflowDefinition: WorkflowDefinition,
  connectionMap: Map<string, string>
): WorkflowDefinition {
  const hasActions = !!workflowDefinition.definition?.actions;
  const hasTriggers = !!workflowDefinition.definition?.triggers;

  if ((!hasActions && !hasTriggers) || connectionMap.size === 0) {
    return workflowDefinition;
  }

  const cloned = JSON.parse(JSON.stringify(workflowDefinition)) as WorkflowDefinition;

  if (hasActions) {
    cloned.definition.actions = patchActionsMap(cloned.definition.actions, connectionMap);
  }

  // Triggers can also reference connections (e.g. ApiConnection triggers)
  if (hasTriggers) {
    cloned.definition.triggers = patchActionsMap(
      cloned.definition.triggers as Record<string, Action>,
      connectionMap
    ) as WorkflowDefinition['definition']['triggers'];
  }

  return cloned;
}

// =============================================================================
// PRIVATE HELPERS
// =============================================================================

/**
 * Recursively patch an actions map, returning a new map with updated actions.
 */
function patchActionsMap(
  actions: Record<string, Action>,
  connectionMap: Map<string, string>
): Record<string, Action> {
  const patched: Record<string, Action> = {};

  for (const [name, action] of Object.entries(actions)) {
    patched[name] = patchAction(action, connectionMap);
  }

  return patched;
}

/**
 * Patch a single action and recurse into any child action maps.
 */
function patchAction(action: Action, connectionMap: Map<string, string>): Action {
  // Apply connection patch to this action's inputs
  const patchedInputs = patchActionInputs(
    action.inputs as Record<string, unknown> | undefined,
    connectionMap
  );

  const baseAction = patchedInputs !== action.inputs
    ? { ...action, inputs: patchedInputs }
    : { ...action };

  // Recurse into scope containers
  if (isIfAction(baseAction)) {
    return patchIfAction(baseAction as IfAction, connectionMap);
  }
  if (isForeachAction(baseAction)) {
    return patchForeachAction(baseAction as ForeachAction, connectionMap);
  }
  if (isSwitchAction(baseAction)) {
    return patchSwitchAction(baseAction as SwitchAction, connectionMap);
  }
  if (isUntilAction(baseAction)) {
    return patchUntilAction(baseAction as UntilAction, connectionMap);
  }
  if (isAgentAction(baseAction)) {
    return patchAgentAction(baseAction as AgentAction, connectionMap);
  }

  return baseAction as Action;
}

/**
 * Patch an If action, recursing into true-branch and false-branch actions.
 */
function patchIfAction(action: IfAction, connectionMap: Map<string, string>): IfAction {
  const patched: IfAction = { ...action };

  if (action.actions) {
    patched.actions = patchActionsMap(action.actions, connectionMap);
  }
  if (action.else?.actions) {
    patched.else = { ...action.else, actions: patchActionsMap(action.else.actions, connectionMap) };
  }

  return patched;
}

/**
 * Patch a Foreach action, recursing into its child actions.
 */
function patchForeachAction(action: ForeachAction, connectionMap: Map<string, string>): ForeachAction {
  if (!action.actions) return action;
  return { ...action, actions: patchActionsMap(action.actions, connectionMap) };
}

/**
 * Patch a Switch action, recursing into each case and the default branch.
 */
function patchSwitchAction(action: SwitchAction, connectionMap: Map<string, string>): SwitchAction {
  const patched: SwitchAction = { ...action };

  const newCases: SwitchAction['cases'] = {};
  for (const [caseName, rawCase] of Object.entries(action.cases)) {
    const switchCase = rawCase as { case: string; actions?: Record<string, Action> };
    newCases[caseName] = switchCase.actions
      ? { ...switchCase, actions: patchActionsMap(switchCase.actions, connectionMap) }
      : switchCase;
  }
  patched.cases = newCases;

  if (action.default?.actions) {
    patched.default = {
      ...action.default,
      actions: patchActionsMap(action.default.actions, connectionMap),
    };
  }

  return patched;
}

/**
 * Patch an Until action, recursing into its child actions.
 */
function patchUntilAction(action: UntilAction, connectionMap: Map<string, string>): UntilAction {
  if (!action.actions) return action;
  return { ...action, actions: patchActionsMap(action.actions, connectionMap) };
}

/**
 * Patch an Agent action, recursing into its tools' inner actions so a
 * tool's ApiConnection / ServiceProvider / nested agent reference gets the
 * same placeholder → real key substitution as a top-level action.
 */
function patchAgentAction(action: AgentAction, connectionMap: Map<string, string>): AgentAction {
  if (!action.tools) return action;
  const patchedTools: Record<string, AgentTool> = {};
  for (const [toolName, tool] of Object.entries(action.tools)) {
    patchedTools[toolName] = tool.actions
      ? { ...tool, actions: patchActionsMap(tool.actions, connectionMap) }
      : tool;
  }
  return { ...action, tools: patchedTools };
}

/**
 * Apply connection reference key substitution to an action's `inputs` object.
 *
 * Handles:
 * - ApiConnection pattern:  `inputs.host.connection.referenceName`
 * - ServiceProvider pattern: `inputs.serviceProviderConfiguration.connectionName`
 * - Agent pattern:           `inputs.modelConfigurations.{modelN}.referenceName`
 *
 * Returns the same object reference if no change was needed.
 */
function patchActionInputs(
  inputs: Record<string, unknown> | undefined,
  connectionMap: Map<string, string>
): Record<string, unknown> | undefined {
  if (!inputs) return inputs;

  // ApiConnection: inputs.host.connection.referenceName
  if (inputs.host) {
    const host = inputs.host as Record<string, unknown>;
    if (host.connection) {
      const conn = host.connection as Record<string, unknown>;
      if (typeof conn.referenceName === 'string') {
        const newKey = resolveKey(conn.referenceName, connectionMap);
        if (newKey !== conn.referenceName) {
          return {
            ...inputs,
            host: {
              ...host,
              connection: { ...conn, referenceName: newKey },
            },
          };
        }
      }
    }
  }

  // ServiceProvider: inputs.serviceProviderConfiguration.connectionName
  if (inputs.serviceProviderConfiguration) {
    const spConfig = inputs.serviceProviderConfiguration as Record<string, unknown>;
    if (typeof spConfig.connectionName === 'string') {
      const newKey = resolveKey(spConfig.connectionName, connectionMap);
      if (newKey !== spConfig.connectionName) {
        return {
          ...inputs,
          serviceProviderConfiguration: { ...spConfig, connectionName: newKey },
        };
      }
    }
  }

  // Agent: inputs.modelConfigurations.{modelN}.referenceName. Without this,
  // templates that ship an agent with a placeholder referenceName (e.g.
  // "agent_#workflowname#") keep that placeholder after resolution, so the
  // canvas reads `referenceKey` → looks it up in the store → miss →
  // createReferenceNotFoundResult → red "error" badge. ApiConnection and
  // ServiceProvider siblings get patched, so they show no badge (or a yellow
  // "warning" when the connection is incomplete). Patching the agent shape
  // here closes that gap.
  if (inputs.modelConfigurations && typeof inputs.modelConfigurations === 'object') {
    const modelConfigs = inputs.modelConfigurations as Record<string, unknown>;
    let nextModelConfigs: Record<string, unknown> | null = null;
    for (const [slot, value] of Object.entries(modelConfigs)) {
      if (!value || typeof value !== 'object') continue;
      const config = value as Record<string, unknown>;
      if (typeof config.referenceName !== 'string') continue;
      const newKey = resolveKey(config.referenceName, connectionMap);
      if (newKey === config.referenceName) continue;
      if (!nextModelConfigs) nextModelConfigs = { ...modelConfigs };
      nextModelConfigs[slot] = { ...config, referenceName: newKey };
    }
    if (nextModelConfigs) {
      return { ...inputs, modelConfigurations: nextModelConfigs };
    }
  }

  return inputs;
}

/**
 * Look up a replacement key from `connectionMap`.
 *
 * Tries exact match first, then normalized (lowercase, alphanumeric-only) match.
 * Returns the original key unchanged if no mapping is found.
 */
function resolveKey(currentKey: string, connectionMap: Map<string, string>): string {
  // Exact match
  if (connectionMap.has(currentKey)) {
    return connectionMap.get(currentKey)!;
  }

  // Normalized match — handles connector type strings like "servicebus" matching
  // placeholder reference names like "ServiceBus_1" in a template
  const normalizedCurrent = currentKey.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const [connectorType, referenceKey] of connectionMap.entries()) {
    const normalizedType = connectorType.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedType === normalizedCurrent) {
      return referenceKey;
    }
  }

  return currentKey;
}
